import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { shallow } from 'zustand/shallow';

import { type TransferSearchTransfersData, type UnifiedTransfer, transferSearchTransfersOptions } from '@column/api';
import { Button, ButtonInner, Dropdown, Icon, Input, Tooltip, formatNumber } from '@column/column-ui-kit';
import {
  Badge,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@column/ui';

import { TransferSnapshotModal } from '../Home/_components/TransferSnapshotModal';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import {
  Loading,
  Pagination,
  PopoverFilterAmount,
  PopoverFilterTransferDate,
  PopoverFilterTransferStatus,
  PopoverFilterTransferType,
  PopoverFilterWrapper,
} from '~/components';
import { ButtonGroupDropdown } from '~/components/ButtonGroupDropdown';
import { SimulateTransferModal, useDialog } from '~/components/Dialog';
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { Search } from '~/components/Search';
import { IconTransfers, LogoLoading } from '~/elements';
import { useCheckTransferId } from '~/hooks/useTransfers';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { toTransferApiQuery, transferTableColumns, useTransfersFilters } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, TransferRepository, transfers } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useScrollStore } from '~/stores/Scroll';
import { useSessionStore } from '~/stores/Session';
import { FormElement, FormLabel, Inner, Paragraph } from '~/styles';
import { downloadBlob, getDateFormat } from '~/util';

import { TransferFiltersModal } from './_components/TransferFiltersModal';

export interface TransferLocationState {
  trigger?: 'delayedFetch';
}

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
`;

// Without these, the input shrinks to its placeholder content despite the `width` prop.
export const TransferSearch = styled(Search)`
  max-width: 100%;
  transition: width 0.2s;

  label {
    flex-grow: 1;
    min-width: 0;
  }

  input {
    min-width: 0;
  }
`;

export const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

export const CreateSnapshot = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const FilterActions = styled.div<{ $isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${({ $isHidden }) => ($isHidden ? 0 : '0 8px')};
  transition: opacity 0.2s;
  width: ${({ $isHidden }) => ($isHidden ? 0 : 'auto')};
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

export const FilterLabel = styled.div`
  align-self: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  margin-right: 8px;
`;

const MobileFiltersWrapper = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (max-width: 1350px) {
    display: flex;
  }
`;

const DesktopFiltersWrapper = styled.div`
  display: contents;

  @media (max-width: 1350px) {
    display: none;
  }
`;

const Toolbar = styled.div<{ $isScrolled: boolean }>`
  display: flex;
  gap: 8px;
  padding: 0 var(--page-margin) 12px;
  justify-content: space-between;
  background: ${({ theme }) => theme.background};
  border-bottom: 1px solid;
  border-bottom-color: transparent;
  position: sticky;
  top: 0;
  z-index: 11;
  margin-top: -1px;
  transition: border-bottom-color 0.2s;

  ${({ $isScrolled }) =>
    $isScrolled &&
    css`
      border-bottom-color: var(--color-border);
    `}
`;

const Fields = styled.div`
  margin-top: 12px;
  display: grid;
  grid-gap: 8px;
`;

const FileNameElement = styled(FormElement)`
  margin-top: 12px;
  text-align: left;
`;

const FileTypeLabel = styled.span`
  position: absolute;
  right: 12px;
  top: 8px;
  line-height: 24px;
  pointer-events: none;
  font-size: 14px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const FilterDropdown = styled(Dropdown)<{ $isFilterActive?: boolean }>`
  table-layout: fixed;
  width: fit-content;
  max-width: 320px;
  min-width: 98px;

  button {
    --button-scale-pressed: 1;
    padding: 8px 10px 8px 12px;
    width: 100%;

    &::after {
      box-shadow:
        0 -1px 1px rgba(255, 255, 255, 0.75) inset,
        ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
          ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
          ${({ theme }) => theme.style.buttonSecondaryBorderColor},
        0 1px 1px rgba(0, 0, 0, 0.075),
        0 1px 4px 0 transparent inset;

      ${({ theme }) =>
        theme.id !== 'default' &&
        css`
          background-color: transparent;
          box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(100)};

          &:hover {
            background-color: ${({}) => theme.secondary.blendToBackground(25)};
            box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(150)};
          }
        `}

      ${({ $isFilterActive }) =>
        $isFilterActive &&
        css`
          box-shadow:
            ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
              ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
              ${({ theme }) => theme.style.buttonSecondaryBorderColor},
            0 0 0 var(--button-outline, 0px)
              var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
            ${({ theme }) => theme.style.buttonSecondaryShadow};
        `}
    }

    &:hover {
      &::after {
        box-shadow:
          0 -1px 1px rgba(255, 255, 255, 0.75) inset,
          ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
            ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
          0 1px 1px rgba(0, 0, 0, 0),
          0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;

        ${({ theme }) =>
          theme.id !== 'default' &&
          css`
            background-color: ${({}) => theme.secondary.blendToBackground(25)};
            box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(150)};
          `}
      }
    }
  }

  ${ButtonInner} {
    transition: 0.2s ease;
    color: ${({ theme, $isFilterActive }) => ($isFilterActive ? theme.primary.background : 'inherit')};
  }

  &:after {
    content: '';
    border-radius: ${({ theme }) => theme.style.buttonSmallBorderRadius};
    z-index: 4;
    position: absolute;
    inset: 0;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px
      ${({ $isFilterActive, theme }) => ($isFilterActive ? theme.primary.background : 'transparent')};
    transition: box-shadow 0.15s;
  }
`;

interface PageTransferExportModalContentProps {
  text: string;
}

export const PageTransferExportModalContent: React.FC<PageTransferExportModalContentProps> = ({ text }) => {
  const { getAlertData, setAlertData } = useDialog();

  return (
    <>
      <Paragraph>{text}</Paragraph>
      <Fields>
        <FileNameElement fullWidth>
          <FormLabel>File type</FormLabel>
          <Dropdown
            active="csv"
            variant="muted"
            fullWidth
            options={[
              {
                label: '.csv (Comma-separated values)',
                value: 'csv',
              },
              {
                label: '.json (JavaScript Object Notation)',
                value: 'json',
                isDisabled: true,
              },
              {
                label: '.txt (BAI2 format)',
                value: 'txt',
                isDisabled: true,
              },
            ]}
          />
        </FileNameElement>
        <FileNameElement fullWidth>
          <FormLabel>
            Custom file name <small>(optional)</small>
          </FormLabel>
          <Input
            value={String(getAlertData('export-transfers')?.customFileName ?? '')}
            onChange={(value) => setAlertData('export-transfers', { customFileName: value })}
            placeholder={`transfers-${getDateFormat(new Date())}`}
          >
            <FileTypeLabel>.csv</FileTypeLabel>
          </Input>
        </FileNameElement>
      </Fields>
    </>
  );
};

export const PageTransfers: React.FC = () => {
  const { isAuthorized, isSandbox, currentPermissionName, currentPlatform } = useSessionStore();
  const { openAlert, closeAlert, getAlertData, setAlertData } = useDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const { addDangerNotification } = useNotificationStore();
  const pageFrameScroll = useScrollStore((s) => s.pageFrameScroll);
  const isScrolled = (pageFrameScroll ?? 0) > 0;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState<boolean>(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const mountedRef = useRef<boolean>(false);
  const [isBelowTablet, setIsBelowTablet] = useState<boolean>(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const locationState = location.state as TransferLocationState;

  const isTransferSearchEnabled = useFeatureFlag(FeatureFlag.EnableTransferSearch);

  const [filterByIdTooltipContent, setFilterByIdTooltipContent] = useState<string>('');
  const [filterByIdValue, setFilterByIdValue] = useState<string>('');

  const isCorrespondentBank = !!currentPlatform?.correspondentBankingEnabled;
  const hasInternationalWires =
    useFeatureFlag(FeatureFlag.HasInternationalWires, { default: true }) || isCorrespondentBank;

  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('transfers');

  const filters = useTransfersFilters({ defaultPageSize: pageSize, isTransferSearchEnabled });

  const allowedTransfers = useMemo(() => {
    return transfers
      .filter((v) => v.id && (v.id !== 'intl-wire' || hasInternationalWires))
      .filter((v) => (isCorrespondentBank ? v.id === 'intl-wire' || v.id === 'fi-transfer' : v.id !== 'fi-transfer'))
      .filter((v) => {
        // Hide Check Scanner below tablet breakpoint
        if (v.id === 'check' && isBelowTablet) {
          return false;
        }
        return true;
      })
      .filter(
        (v) =>
          v.permissionName &&
          v.permissionName.some((permission) =>
            isAuthorized({
              permission,
              permissionLevel: 'write',
            })
          )
      );
  }, [isAuthorized, isBelowTablet, isCorrespondentBank]);

  useEffect(() => {
    if (!filters.queryParams) {
      return undefined;
    }

    const { counterpartyId, accountNumberId } = filters.queryParams;

    setFilterByIdValue(counterpartyId || accountNumberId || '');
  }, []);

  const handleFilterByIdValueChange = useCallback((value: string) => {
    setFilterByIdValue(value);
    setFilterByIdTooltipContent('');
  }, []);

  const handleQueryInputChange = useCallback(
    (value: string) => {
      setFilterByIdTooltipContent('');
      filters.handleQueryInputChange(value);
    },
    [filters.handleQueryInputChange]
  );

  const {
    options: bankAccountList,
    hiddenOptions: bankAccountHiddenOptions,
    search: searchBankAccounts,
  } = useBankAccountOptions({ value: filters.bankAccountIdFilterData });

  const isFilterActive = useMemo(
    () => Boolean(filters.isFilterActive || (!isTransferSearchEnabled && filterByIdValue)),
    [filters.isFilterActive, isTransferSearchEnabled, filterByIdValue]
  );
  const isEmptyStateFilterActive = useMemo(
    () => Boolean(Object.keys(filters.filters).length > 0 || (!isTransferSearchEnabled && filterByIdValue)),
    [filters.filters, isTransferSearchEnabled, filterByIdValue]
  );

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      setIsBelowTablet(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFilterActive]);

  const checkTransferId = useCheckTransferId({
    onSuccess: (id) => {
      const url = transfers.find((transfer) => transfer.idString && id.startsWith(transfer.idString))?.id;
      navigate(`${ROUTE.TRANSFERS}/${url}/${id}`);
    },
    onError: (message) => {
      setFilterByIdTooltipContent(message);
    },
  });

  const apiQuery = useMemo<NonNullable<TransferSearchTransfersData['query']>>(
    () => ({
      limit: pageSize,
      ...(filters.cursor ? { starting_after: filters.cursor } : {}),
      ...toTransferApiQuery(filters.filters),
    }),
    [pageSize, filters.cursor, filters.filters]
  );

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...transferSearchTransfersOptions({ query: apiQuery }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const transferList = useMemo<UnifiedTransfer[]>(() => response?.transfers ?? [], [response]);
  const hasMore = response?.has_more ?? false;
  const totalResultsCount = response?.total_results_count ?? 0;

  const singleResult = useMemo<UnifiedTransfer | null>(() => {
    if (isFetching) return null;
    return transferList.length === 1 && !hasMore ? transferList[0] : null;
  }, [transferList, hasMore, isFetching]);

  // Reset pagination cursor when any filter changes so we don't keep paging from a cursor
  // that belongs to a different result set.
  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    filters.resetPagination();
  }, [
    filters.debouncedQueryInput,
    filters.bankAccountIdFilterData,
    filters.queryParams?.counterpartyId,
    filters.queryParams?.accountNumberId,
    filters.queryParams?.transferBatchId,
    filters.transferTypeFilterData,
    filters.transferStatusFilterData,
    filters.amountFilterData,
    filters.transferDateFilterData,
    filters.resetPagination,
  ]);

  const showEnterHint = useMemo(
    () =>
      isTransferSearchEnabled &&
      filters.queryInput.length > 0 &&
      filters.debouncedQueryInput === filters.queryInput &&
      singleResult !== null,
    [isTransferSearchEnabled, filters.debouncedQueryInput, filters.queryInput, singleResult]
  );

  const handleQuerySubmit = useCallback(
    (value: string) => {
      if (showEnterHint && singleResult) {
        navigate(getTransferURL(singleResult.id, singleResult.type as TransferEntryType));
        return;
      }

      filters.cancelApplyQuery();
      filters.setQueryInput(value);
      filters.setDebouncedQueryInput(value);
    },
    [filters.cancelApplyQuery, navigate, showEnterHint, singleResult]
  );

  const handleFilterById = useCallback(
    (value: string) => {
      if (value.length > 0 && transfers.some((transfer) => transfer.idString && value.startsWith(transfer.idString))) {
        checkTransferId(value);

        return;
      }

      if (value.startsWith('cpty_')) {
        filters.setBankAccountIdFilterData('');
        filters.setUrlQueryParams((prev) => ({
          ...(prev ?? {}),
          counterpartyId: value,
          accountNumberId: undefined,
        }));

        return;
      }

      if (value.startsWith('bacc_')) {
        filters.setBankAccountIdFilterData(value);
        filters.setUrlQueryParams((prev) => ({
          ...(prev ?? {}),
          counterpartyId: undefined,
          accountNumberId: undefined,
        }));

        return;
      }

      if (value.startsWith('acno_')) {
        filters.setBankAccountIdFilterData('');
        filters.setUrlQueryParams((prev) => ({
          ...(prev ?? {}),
          accountNumberId: value,
          counterpartyId: undefined,
        }));

        return;
      }

      if (value && value.length > 0) {
        setFilterByIdTooltipContent('Not a supported ID. Enter a Bank Account ID, Counterparty ID, or Transfer ID.');
      } else {
        filters.setUrlQueryParams(() => undefined);
        setFilterByIdTooltipContent('');
      }
    },
    [checkTransferId, filters.setUrlQueryParams]
  );

  useLayoutEffect(() => {
    if (locationState?.trigger === 'delayedFetch') {
      window.history.replaceState({}, '');
      setTimeout(() => {
        refetch();
      }, 300);
    }
  }, [locationState?.trigger, refetch]);

  const handleFilterReset = useCallback(() => {
    filters.resetAllFilters();
    setFilterByIdValue('');
    setFilterByIdTooltipContent('');
  }, [filters.resetAllFilters]);

  const [exportAnimate, setExportAnimate] = useState<boolean>(false);
  const [entriesOpen, setEntriesOpen] = useState<boolean>(false);

  const activePageSize = Number(pageSize);
  const tableMinHeight = transferList.length === 0 ? 341 : undefined;

  const handleExport = () => {
    if (getAlertData('export-transfers')?.submitLoading) {
      return;
    }

    setAlertData('export-transfers', { submitLoading: true });
    setExportAnimate(true);

    TransferRepository.export(filters.filters)
      .then((csv) => {
        downloadBlob(
          csv,
          `${
            String(getAlertData('export-transfers')?.customFileName).length > 0
              ? getAlertData('export-transfers')?.customFileName
              : `transfers-${getDateFormat(new Date())}`
          }.csv`
        );
      })
      .then(() => {
        closeAlert();
      })
      .catch((e) =>
        addDangerNotification({
          content: e.message,
          display: 'popup',
        })
      )
      .finally(() => {
        setAlertData('export-transfers', { submitLoading: false });
      });
  };

  const handleExportClick = () => {
    let text = `Do you want to export & download the .csv file for ${formatNumber(totalResultsCount, false)} transfers?`;

    if (!isFilterActive) {
      text = `Are you sure you want to export & download the .csv file for all transfers (${formatNumber(
        totalResultsCount,
        false
      )}) without any filters applied?`;
    }

    if (totalResultsCount > 1000) {
      text = `Are you sure to export & download the .csv file for ${formatNumber(
        totalResultsCount,
        false
      )} transfers? This may take a while.`;
    }

    openAlert({
      headline: 'Export',
      content: <PageTransferExportModalContent text={text} />,
      type: 'primary',
      confirmButton: {
        label: 'Download',
        variant: 'primary',
      },
      onSuccess: handleExport,
    });
  };

  useEffect(() => {
    setTimeout(() => {
      mountedRef.current = true;
    }, 2000);
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        () => {
          if (!mountedRef.current) {
            return;
          }

          handleFilterReset();
        },
        {
          equalityFn: shallow,
        }
      ),
    [mountedRef.current]
  );

  const platformRole = useSessionStore((state) => state.currentPermissionName);

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const columns = useMemo(
    () => transferTableColumns({ timeFormat, onTimeFormatChange: handleTimeFormatChange }),
    [timeFormat, handleTimeFormatChange]
  );

  const handlePageNext = useCallback(() => {
    if (transferList.length > 0) {
      filters.onNext({ cursor: transferList[transferList.length - 1].id });
    }
  }, [transferList, filters.onNext]);

  const handlePageSizeChange = useCallback(
    (size: number) => {
      filters.setPageSize(size);
      updateTableConfig({ limit: size }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [filters.setPageSize, updateTableConfig, addDangerNotification]
  );

  return (
    <>
      <TransferSnapshotModal isOpen={isOpen} onClose={() => setIsOpen(false)} filters={filters.filters} />

      <SimulateTransferModal
        isOpen={isSandboxModalOpen}
        onClose={() => setIsSandboxModalOpen(false)}
        onSuccess={() => {
          setTimeout(() => refetch(), 200);
        }}
      />

      <TransferFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        bankAccountList={bankAccountList}
        bankAccountHiddenOptions={bankAccountHiddenOptions}
        searchBankAccounts={(description: string) => searchBankAccounts(description)}
        initialBankAccountId={filters.bankAccountIdFilterData}
        initialAmount={filters.amountFilterData}
        initialType={{
          isIncoming: filters.transferTypeFilterData.isIncoming,
          type: filters.transferTypeFilterData.type as unknown as string[],
        }}
        initialStatus={filters.transferStatusFilterData}
        initialDate={{
          createdAtGte: filters.transferDateFilterData?.createdAtGte,
          createdAtLt: filters.transferDateFilterData?.createdAtLt,
        }}
        isTransferSearchEnabled={isTransferSearchEnabled}
        initialQueryValue={isTransferSearchEnabled ? filters.debouncedQueryInput : filterByIdValue}
        onApply={(values) => {
          filters.setBankAccountIdFilterData(values.bankAccountId);
          filters.setAmountFilterData({
            amount: values.amount,
            amountGte: values.amountGte,
            amountLte: values.amountLte,
          });
          filters.setTransferTypeFilterData({
            isIncoming: values.isIncoming,
            type: values.type as unknown as typeof filters.transferTypeFilterData.type,
          });
          filters.setTransferStatusFilterData(values.status);
          filters.setTransferDateFilterData({ createdAtGte: values.createdAtGte, createdAtLt: values.createdAtLt });
          const query = values.query ?? '';
          if (isTransferSearchEnabled) {
            filters.cancelApplyQuery();
            filters.setQueryInput(query);
            filters.setDebouncedQueryInput(query);
          } else if (query.length > 0) {
            handleFilterById(query);
          }
        }}
      />
      <PageActions>
        {isSandbox && (
          <Button onClick={() => setIsSandboxModalOpen(true)} icon={<Icon.Sandbox />} size="small">
            Simulate
          </Button>
        )}
        {allowedTransfers.length > 0 && (
          <Tooltip
            content={`With your ${currentPermissionName} role, access here is limited.`}
            isOpen={allowedTransfers.length === 0 ? undefined : false}
            placement="left"
          >
            <ButtonGroupDropdown
              key={hasInternationalWires ? 'intl-wire' : 'wire'}
              buttons={[
                {
                  label: 'Create Transfer',
                  icon: <Icon.Plus />,
                  isDisabled: allowedTransfers.length === 0,
                  onClick: () =>
                    navigate(`${ROUTE.TRANSFERS}/edit/${platformRole === 'Check Depositor' ? 'check' : 'ach'}`),
                },
              ]}
              options={allowedTransfers.map((transfer) => {
                const IconElement = IconTransfers[transfer.icon];
                return {
                  icon: <IconElement />,
                  label: transfer.label,
                  onClick: () => {
                    navigate(`${ROUTE.TRANSFERS}/edit/${transfer.id}`);
                  },
                };
              })}
            />
          </Tooltip>
        )}
      </PageActions>

      <Toolbar $isScrolled={isScrolled}>
        <MobileFiltersWrapper>
          <Button ref={filtersButtonRef} size="small" variant="secondary" onClick={() => setIsFiltersOpen(true)}>
            <Icon.Filter />
            Filters
          </Button>

          {isFilterActive && (
            <FilterActions $isHidden={!isFilterActive}>
              <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                Reset
              </ResetFilter>
              <CreateSnapshot
                size="small"
                variant="subtle-primary"
                icon={<Icon.Plus />}
                onClick={() => setIsOpen(true)}
              >
                New Snapshot
              </CreateSnapshot>
            </FilterActions>
          )}
        </MobileFiltersWrapper>
        <DesktopFiltersWrapper>
          <FilterDropdown
            options={bankAccountList}
            hiddenOptions={bankAccountHiddenOptions}
            size="small"
            active={filters.bankAccountIdFilterData}
            $isFilterActive={!!filters.bankAccountIdFilterData}
            label={
              bankAccountHiddenOptions?.find((b) => b.value === filters.bankAccountIdFilterData)?.label || 'Account'
            }
            search
            fullWidth
            searchLabel="Search accounts"
            onSearchChange={(description: string) => searchBankAccounts(description)}
            onChange={(value) => filters.setBankAccountIdFilterData(value)}
          />
          <PopoverFilterWrapper
            label="Amount"
            onSubmit={(data) => filters.setAmountFilterData(data)}
            isActive={filters.isAmountFilterActive}
          >
            <PopoverFilterAmount data={filters.amountFilterData ?? {}} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper
            label="Type"
            onSubmit={(data) => filters.setTransferTypeFilterData(data)}
            isActive={filters.isTransferTypeFilterActive}
          >
            <PopoverFilterTransferType data={filters.transferTypeFilterData ?? {}} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper
            label="Status"
            onSubmit={(data) => filters.setTransferStatusFilterData(data)}
            isActive={filters.transferStatusFilterData && filters.transferStatusFilterData.length > 0}
          >
            <PopoverFilterTransferStatus data={filters.transferStatusFilterData ?? []} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper
            label="Date"
            onSubmit={(data) => filters.setTransferDateFilterData(data)}
            isActive={filters.isDateFilterActive}
          >
            <PopoverFilterTransferDate data={filters.transferDateFilterData ?? {}} />
          </PopoverFilterWrapper>
          {isTransferSearchEnabled ? (
            <TransferSearch
              value={filters.queryInput}
              onValueChange={handleQueryInputChange}
              onFilterSubmit={handleQuerySubmit}
              placeholder="Search transfers..."
              tooltipContent={filterByIdTooltipContent}
              showEnterHint={showEnterHint}
              width={250}
            />
          ) : (
            <Search
              value={filterByIdValue}
              onFilterSubmit={handleFilterById}
              placeholder="Filter by ID"
              tooltipContent={filterByIdTooltipContent}
              onValueChange={handleFilterByIdValueChange}
            />
          )}

          {isFilterActive && (
            <FilterActions $isHidden={!isFilterActive}>
              <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                Reset
              </ResetFilter>
              <CreateSnapshot
                size="small"
                variant="subtle-primary"
                icon={<Icon.Plus />}
                onClick={() => setIsOpen(true)}
              >
                New Snapshot
              </CreateSnapshot>
            </FilterActions>
          )}
        </DesktopFiltersWrapper>

        <Buttons>
          <Button
            icon={<Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />}
            size="small"
            variant="secondary"
            onClick={handleExportClick}
          >
            Export
          </Button>
        </Buttons>
      </Toolbar>

      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UnifiedTransfer>
            columns={columns}
            data={transferList}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            loadingContent={<LogoLoading />}
            style={{ minHeight: tableMinHeight }}
            emptyContent={
              <Empty className="min-h-[300px]">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon.AnimationArrows />
                  </EmptyMedia>
                  <EmptyTitle>No transfers found</EmptyTitle>
                  <EmptyDescription>
                    {isEmptyStateFilterActive
                      ? 'No transfers were found matching the specified filters'
                      : 'Create a transfer to get started.'}
                  </EmptyDescription>
                </EmptyHeader>
                {!isEmptyStateFilterActive && (
                  <EmptyContent className="pointer-events-auto">
                    <Button
                      onClick={() =>
                        navigate(`${ROUTE.TRANSFERS}/edit/${platformRole === 'Check Depositor' ? 'check' : 'ach'}`)
                      }
                      size="small"
                      icon={<Icon.Plus />}
                    >
                      Create Transfer
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            }
            rowClassName="min-h-11"
            onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
          />

          <div className="relative z-[1] flex items-center justify-between pt-4">
            <Dropdown
              options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                label: entries.toString(),
                value: entries,
              }))}
              size="small"
              variant="muted"
              active={activePageSize}
              customLabel={
                <Badge
                  variant="secondary"
                  className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                >
                  {activePageSize}
                  {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                </Badge>
              }
              onOpenChange={setEntriesOpen}
              onChange={handlePageSizeChange}
            />
            <Pagination
              current={filters.pageIndex + 1}
              disablePrev={!filters.hasPrevious}
              disableNext={!hasMore}
              onPrev={filters.onPrevious}
              onNext={handlePageNext}
            />
          </div>
        </Inner>
      </Loading>
    </>
  );
};
